<template>
    <div class="relative">
        <ListItemButton
            :dusk="`${duskPrefix}Chicago`"
            class="text-theme-toolbar-icon"
            :aria-expanded="showSubmenu"
            aria-haspopup="menu"
            @click="toggleSubmenu"
            @mouseenter="showSubmenu = true"
            @mouseleave="handleMouseLeave"
        >
            <div class="flex w-full items-center" :class="{ 'justify-between': chevronPosition === 'right' }">
                <svg-icon
                    v-if="chevronPosition === 'left'"
                    icon="chevron-left"
                    class="mr-2"
                    width="16px"
                    height="16px"
                />
                <span>{{ labels.Chicago }}</span>
                <svg-icon v-if="chevronPosition === 'right'" icon="chevron-right" width="16px" height="16px" />
            </div>
        </ListItemButton>
        <transition name="slide-fade">
            <div
                v-show="showSubmenu"
                :class="['absolute -top-16 z-[70]', effectiveSubmenuPositionClass]"
                @mouseenter="showSubmenu = true"
                @mouseleave="showSubmenu = false"
            >
                <ListDropdown role="menu" class="w-[194px]">
                    <ListItemButton
                        v-for="subformat in subformats"
                        :key="subformat"
                        :dusk="`${duskPrefix}${subformat}`"
                        class="py-3 text-theme-toolbar-icon"
                        @click="selectSubformat(subformat)"
                    >
                        {{ labels[subformat] }}
                    </ListItemButton>
                </ListDropdown>
            </div>
        </transition>
    </div>
</template>

<script>
import ListItemButton from './ListItemButton.vue';
import ListDropdown from './ListDropdown.vue';

export default {
    components: {
        ListItemButton,
        ListDropdown,
    },
    props: {
        subformats: {
            type: Array,
            required: true,
        },
        labels: {
            type: Object,
            required: true,
        },
        duskPrefix: {
            type: String,
            required: true,
        },
        submenuPositionClass: {
            type: String,
            required: true,
        },
        chevronPosition: {
            type: String,
            required: true,
            validator: value => ['left', 'right'].includes(value),
        },
    },
    emits: ['select'],
    data() {
        return {
            showSubmenu: false,
            // Flipped so the side submenu opens leftwards when opening rightwards would overflow the screen.
            openToStart: false,
        };
    },
    computed: {
        effectiveSubmenuPositionClass() {
            return this.openToStart ? 'right-full mr-3' : this.submenuPositionClass;
        },
    },
    watch: {
        showSubmenu(isOpen) {
            if (isOpen) {
                this.$nextTick(this.adjustSide);
            }
        },
    },
    methods: {
        /**
         * Decide which side the submenu opens on from the trigger's geometry (stable, no flip-flop).
         * The panel is 194px wide with a 12px gap; prefer opening rightwards and only flip leftwards
         * when the right side overflows and the left side has room.
         * @returns {void}
         */
        adjustSide() {
            const root = this.$el;
            if (!root) return;

            const SUBMENU_WIDTH = 194;
            const GAP = 12;
            const SCREEN_MARGIN = 8;
            const rootRect = root.getBoundingClientRect();
            const rightEdgeWhenOpeningRight = rootRect.right + GAP + SUBMENU_WIDTH;
            const leftEdgeWhenOpeningLeft = rootRect.left - GAP - SUBMENU_WIDTH;

            this.openToStart =
                rightEdgeWhenOpeningRight > window.innerWidth - SCREEN_MARGIN &&
                leftEdgeWhenOpeningLeft >= SCREEN_MARGIN;
        },
        /**
         * Toggles the Chicago submenu visibility.
         */
        toggleSubmenu() {
            this.showSubmenu = !this.showSubmenu;
        },
        /**
         * Hides the submenu. Intended to be called from the parent via $refs.
         */
        hideSubmenu() {
            this.showSubmenu = false;
        },
        /**
         * Handles mouse leave with a small delay to allow cursor movement to the submenu.
         */
        handleMouseLeave() {
            setTimeout(() => {
                if (!this.$el.querySelector('.absolute.left-full:hover, .absolute.right-full:hover')) {
                    this.showSubmenu = false;
                }
            }, 100);
        },
        /**
         * Emits the selected subformat and hides the submenu.
         * @param {string} subformat - The selected Chicago subformat key
         */
        selectSubformat(subformat) {
            this.$emit('select', subformat);
            this.showSubmenu = false;
        },
    },
};
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    opacity: 0;
    transform: translateX(10px);
}
</style>
